import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import LocationCompanySelector from '../Common/LocationCompanySelector';
import { TrendingUp, X} from "lucide-react";
import { useSuperAdmin } from "../../context/SuperAdminContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const COLOR = "3B82F6";


const USE_API = true;

const PENDING_STATUS = "pending";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("superadmintoken")}`,
});

const getErrorMessage = (data, fallback) =>
  data?.error || data?.details?.message || data?.message || fallback;

const formatDate = (dateStr) => {
  if (!dateStr) return "-";

  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const formatTime = (date) => {
  return new Date(date).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const displayName = (field, fallbackKey) => {
  if (!field) return "-";
  if (typeof field === "object") return field.name || field.title || "-";
  return fallbackKey ?? "-";
};

// The API can return the accessory either as a populated `accessoryId`
// (older shape) or as a separate `accessory` object (current shape, seen in
// /accessories-history — includes name/logo/isReturnable/defaultPrice).
const getAccessoryObj = (r) =>
  (r?.accessory && typeof r.accessory === "object" && r.accessory) ||
  (r?.accessoryId && typeof r.accessoryId === "object" && r.accessoryId) ||
  null;

const isReturnable = (item) => {
  const accessoryObj = getAccessoryObj(item);
  if (accessoryObj) {
    if ("isReturnable" in accessoryObj) return accessoryObj.isReturnable !== false;
    if ("returnable" in accessoryObj) return accessoryObj.returnable !== false;
  }
  if ("returnable" in (item || {})) return item.returnable !== false;
  return true;
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "-");

const wait = (ms = 500) => new Promise((res) => setTimeout(res, ms));

// ── Small accessory avatar (logo) ────────────────────────────────────────
const AccessoryLogo = ({ src, alt }) => {
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    return (
      <span className="w-6 h-6 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] font-semibold text-gray-400 shrink-0">
        {alt ? alt.charAt(0).toUpperCase() : "?"}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt || "accessory"}
      onError={() => setErrored(true)}
      className="w-6 h-6 rounded-full object-cover border border-gray-200 shrink-0"
    />
  );
};

// ── Reusable Field Wrapper ────────────────────────────────────────────
const Field = ({ label, required, error, children }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-800 mb-2">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {children}
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

const Input = ({ type = "text", hasError, ...props }) => (
  <input
    type={type}
    {...props}
    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-7 text-sm ${
      hasError ? "border-red-400 bg-red-50" : "border-gray-300"
    }`}
  />
);

// ── Dummy data ───────────────────────────────────────────────────────────
const DUMMY_ACCESSORIES = [
  { _id: "acc_1", name: "Helmet", originalValue: 250 },
  { _id: "acc_2", name: "Gloves", originalValue: 80 },
  { _id: "acc_3", name: "Shoes",  originalValue: 600 },
];

const DUMMY_ACCESSORY_RECORDS = [
  {
    _id: "occ_1",
    accessory: { _id: "acc_1", name: "Helmet", isReturnable: false, logo: "" },
    companyId: { _id: "co_1", name: "Lenskart (BDLK)" },
    AllotedAmount: 250,
    AllotedBy: { name: "Harsh" },
    AllotedOn: "2026-08-10T10:00:00.000Z",
    status: "pending",
    ReturnedAmount: 0,
  },
  {
    _id: "occ_2",
    accessory: { _id: "acc_2", name: "Gloves", isReturnable: true, logo: "" },
    companyId: { _id: "co_1", name: "Lenskart (BDLK)" },
    AllotedAmount: 100,
    AllotedBy: { name: "Harsh" },
    AllotedOn: "2026-08-15T14:30:00.000Z",
    status: "pending",
    ReturnedAmount: 0,
  },
  {
    _id: "hist_1",
    accessory: { _id: "acc_1", name: "Helmet", isReturnable: true, logo: "" },
    companyId: { _id: "co_2", name: "Bony Polymer(MABO)" },
    AllotedAmount: 250,
    AllotedBy: { name: "Annanay" },
    AllotedOn: "2026-08-10T10:00:00.000Z",
    status: "returned",
    ReturnedAmount: 200,
    ReturnedBy: { name: "Annanay" },
    ReturnedOn: "2026-08-30T14:30:00.000Z",
  },
];

// ── Add Accessory sub-modal
function AddAccessoryModal({ workerId, onClose, onAssigned }) {

  const {fetchaccessoriesbycompany  ,  allot_accessory}=useSuperAdmin()
  const [selectedLocationName, setSelectedLocationName] = useState("");
  const [selectedLocationObj, setSelectedLocationObj] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedCompanyName, setSelectedCompanyName] = useState("");
  const [accessories, setAccessories] = useState([]);
  const [loadingAccessories, setLoadingAccessories] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);

  const loadAccessories = useCallback(async (companyId) => {
    setLoadingAccessories(true);
    setAccessories([]);
    try {
      if (!USE_API) {
        await wait();
        setAccessories(DUMMY_ACCESSORIES);
        return;
      }
      const res = await fetchaccessoriesbycompany(companyId);
      if (!res.data.success) throw new Error(getErrorMessage(res.data, "Failed to load Essential"));
      setAccessories(res.data.accessories || []);
    } catch (err) {
      toast.error(err.message || "Failed to load Essential");
    } finally {
      setLoadingAccessories(false);
    }
  }, []);

  useEffect(() => {
    setAccessories([]);
    setSelectedIds([]);
  }, [selectedLocationName]);

  useEffect(() => {
    const locationId = selectedLocationObj?._id;
    if (locationId && selectedCompanyId) {
      setSelectedIds([]);
      loadAccessories(selectedCompanyId);
    }
  }, [selectedLocationObj, selectedCompanyId, loadAccessories]);

  const toggleAccessory = (accessoryId) => {
    setSelectedIds((prev) =>
      prev.includes(accessoryId) ? prev.filter((id) => id !== accessoryId) : [...prev, accessoryId]
    );
  };

  const handleAssign = async () => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one Essential to assign");
      return;
    }
    setIsAssigning(true);
    try {
      if (!USE_API) {
        await wait();
        toast.success("Essentials assigned to worker!");
        onAssigned();
        onClose();
        return;
      }
      const res = await allot_accessory(
        selectedCompanyId,
        workerId,
        selectedIds
      );
      const data = res.data;
      if (data.success === false) {
        throw new Error(getErrorMessage(data, "Failed to assign Essential"));
      }
      toast.success("Essentials assigned to worker!");
      onAssigned();
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to assign Essential");
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[70] p-4" style={{ fontFamily: "'Segoe UI', sans-serif" }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-900">Assign Essentials</h2>
          <button onClick={onClose} className="text-sm font-semibold text-gray-500 hover:text-gray-700 transition-all">
             <X size={24} className="text-gray-600" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <LocationCompanySelector
            selectedLocation={selectedLocationName}
            SelectedLocationobject={selectedLocationObj}
            setSelectedLocationobject={setSelectedLocationObj}
            setSelectedLocation={setSelectedLocationName}
            selectedCompany={selectedCompanyId}
            setSelectedCompany={setSelectedCompanyId}
            setSelectedCompanyname={setSelectedCompanyName}
          />

          <Field label="Available Essentials">
            {!selectedCompanyId ? (
              <p className="text-xs text-gray-400 text-center py-6">Select company to see Essentials</p>
            ) : loadingAccessories ? (
              <p className="text-xs text-gray-400 text-center py-6">Loading Essentials...</p>
            ) : accessories.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-6">No Essentials available for this company</p>
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto">
                {accessories.map((acc) => {
                  const isSelected = selectedIds.includes(acc._id);
                  return (
                    <label
                      key={acc._id}
                      className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm cursor-pointer hover:bg-gray-50 transition-all"
                      style={{ background: isSelected ? "#EFF6FF" : "transparent" }}
                    >
                      <div className="flex items-center gap-2.5">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleAccessory(acc._id)}
                          className="accent-blue-600"
                        />
                        <AccessoryLogo src={acc.logo} alt={acc.name} />
                        <span className="font-medium text-gray-800">{acc.name}</span>
                      </div>
                      {acc.accessoriesPrice != null && (
                        <span className="text-xs text-gray-400">₹{acc.accessoriesPrice}</span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </Field>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            disabled={isAssigning}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAssign}
            disabled={isAssigning || selectedIds.length === 0}
            className="px-5 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60 transition-all"
            style={{ background: `#${COLOR}` }}
          >
            {isAssigning ? "Assigning..." : `Assign${selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function RefundModal({ item, workerId, onClose, onRefunded }) {
  const { return_accessory } = useSuperAdmin();
  const maxRefund = Number(item.originalValue ?? 0);
  const [price, setPrice] = useState(String(item.originalValue ?? 0));
  const [remark, setRemark] = useState("");
  const [isRefunding, setIsRefunding] = useState(false);
 
  const priceNum = Number(price);
  const priceError =
    price.trim() === ""
      ? null
      : Number.isNaN(priceNum) || priceNum < 0
      ? "Enter a valid refund price"
      : priceNum > maxRefund
      ? `Refund can't exceed the allotted amount (₹${maxRefund})`
      : null;
 
  const handleConfirm = async () => {
    if (Number.isNaN(priceNum) || priceNum < 0) {
      toast.error("Enter a valid refund price");
      return;
    }
    if (priceNum > maxRefund) {
      toast.error(`Refund price can't be more than the allotted amount (₹${maxRefund})`);
      return;
    }
    if (!remark.trim()) {
      toast.error("Please add a remark");
      return;
    }
    setIsRefunding(true);
    try {
      if (!USE_API) {
        await wait();
        toast.success("Refund processed successfully!");
        onRefunded();
        onClose();
        return;
      }
      const res = await return_accessory(item._id, priceNum, remark.trim());
      const data = res.data;
      if (data.success === false) {
        throw new Error(getErrorMessage(data, "Failed to process refund"));
      }
      toast.success("Refund processed successfully!");
      onRefunded();
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to process refund");
    } finally {
      setIsRefunding(false);
    }
  };
 
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[70] p-4" style={{ fontFamily: "'Segoe UI', sans-serif" }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
        
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Refund Accessory</h2>
          <button onClick={onClose} className="text-sm font-semibold text-gray-500 hover:text-gray-700 transition-all">
            <X size={24} className="text-gray-600" />
          </button>
          </div>
                    <div>
            <div className="flex items-center gap-2 mt-1">
              <AccessoryLogo src={item.accessoryLogo} alt={item.accessoryName} />
              <p className="text-sm font-semibold text-gray-800">{item.accessoryName}</p>
            </div>
          </div>
          
        </div>
 
        <div className="p-3 space-y-2">
 
 
          <Field label="Refund Amount" required error={priceError}>
            <Input
              type="number"
              min="0"
              max={maxRefund}
              hasError={!!priceError}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
            <p className="text-xs text-gray-400 mt-1">Allotted amount: ₹{maxRefund}</p>
          </Field>
 
          <Field label="Remark" required>
            <textarea
              rows={3}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Reason for deduction"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all resize-none"
            />
          </Field>
        </div>
 
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isRefunding}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isRefunding || !!priceError || !remark.trim()}
            className="px-5 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60 transition-all"
            style={{ background: `#${COLOR}` }}
          >
            {isRefunding ? "Processing..." : "Confirm Refund"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Delete confirm sub-modal ─────────────────────────────────────────────
function DeleteConfirmModal({ item, onClose, onConfirm, isDeleting }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[70] p-4" style={{ fontFamily: "'Segoe UI', sans-serif" }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Remove Essential</h2>
        </div>
        <div className="p-6">
<p className="text-sm text-gray-600">
  Remove <span className="font-medium">{item.accessoryName}</span> from this worker's assigned Essentials?
</p>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-5 py-2 rounded-lg text-sm font-semibold text-white bg-red-500 hover:bg-red-600 disabled:opacity-60 transition-all"
          >
            {isDeleting ? "Removing..." : "Remove"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main modal ─────────────────────────────────────────────────────────
const AccessoryManageModal = ({ isOpen, onClose, workerId, workerName = "", workerCode = "" , onRefresh}) => {

  // NOTE: fetchaccessorieshistory now backs BOTH the "Assigned Accessories"
  // and "Taken History" sections — we split the single response by `status`.
  // Each record's accessory details come back under `accessory`
  // (name / logo / isReturnable / defaultPrice), not a populated `accessoryId`.
  const { fetchaccessorieshistory, delete_accessory } = useSuperAdmin();

  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [refundItem, setRefundItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const resetAll = useCallback(() => {
    setRecords([]);
    setShowAddModal(false);
    setRefundItem(null);
    setDeleteItem(null);
  }, []);

  const handleClose = () => {
    resetAll();
    onClose();
    onRefresh();
  };

  const loadRecords = useCallback(async () => {
    if (!workerId) return;
    setLoadingRecords(true);
    try {
      if (!USE_API) {
        await wait();
        setRecords(DUMMY_ACCESSORY_RECORDS);
        return;
      }
      const res = await fetchaccessorieshistory(workerId);
      const data = res.data;
      if (!data.success) throw new Error(getErrorMessage(data, "Failed to load accessories"));
      setRecords(data.data || data.history || []);
    } catch (err) {
      toast.error(err.message || "Failed to load accessories");
    } finally {
      setLoadingRecords(false);
    }
  }, [workerId, fetchaccessorieshistory]);

  useEffect(() => {
    if (isOpen) {
      loadRecords();
    } else {
      resetAll();
    }
  }, [isOpen, workerId, loadRecords, resetAll]);

  const refreshLists = () => {
    loadRecords();
  };

  // ── Normalize a raw record from the API into the shape the UI needs ────
// ── Normalize a raw record from the API into the shape the UI needs ────
// ── Normalize a raw record from the API into the shape the UI needs ────
const normalize = (r) => {
  const accessoryObj = getAccessoryObj(r);
  return {
    _id: r._id,
    accessoryName: displayName(accessoryObj, r.accessoryName),
    accessoryLogo: accessoryObj?.logo || r.accessoryLogo || null,
    companyName: displayName(r.companyId, r.companyName),
    assignedBy: displayName(r.AllotedBy, r.assignedBy),
    assignedOn: r.AllotedOn || null,          // assigned time
    allotedOn: r.AllotedEntryTime || null,    // alloted date & time
    originalValue: r.AllotedAmount ?? r.originalValue ?? 0,
    status: r.status,
    returnable: isReturnable(r),
    returnedOn: r.ReturnedOn || null,         // returned time
    refundBy: r?.RefundedBy?.name || "",
    refundedOn: r.deductedEntryTime || null,  // refunded time
    refundedValue: r.ReturnedAmount ?? r.refundedValue ?? 0,
    remarks: r?.ReturnedRemarks || "",
  };
};

const REFUND_WINDOW_DAYS = 7;

const isWithinRefundWindow = (dateStr, days = REFUND_WINDOW_DAYS) => {
  if (!dateStr) return false;
  const t = new Date(dateStr).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t <= days * 24 * 60 * 60 * 1000;
};

  const occupiedItems = useMemo(
    () => records.filter((r) => (r.status === PENDING_STATUS || r.status === "alloted") ).map(normalize),
    [records]
  );

  const takenHistory = useMemo(
    () => records.filter((r) => r.status == "refunded" || r.status == "returned").map(normalize),
    [records]
  );

  const handleDeleteConfirm = async () => {
    if (!deleteItem) return;
    setIsDeleting(true);
    try {
      if (!USE_API) {
        await wait();
        toast.success("Essential removed! (dummy)");
        setDeleteItem(null);
        refreshLists();
        return;
      }
      const res = await delete_accessory(deleteItem._id);
      const data = res.data;
      if (data.success === false) {
        throw new Error(getErrorMessage(data, "Failed to remove Essential"));
      }
      toast.success("Essential Removed!");
      setDeleteItem(null);
      refreshLists();
    } catch (err) {
      toast.error(err.message || "Failed to remove Essential");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4" style={{ fontFamily: "'Segoe UI', sans-serif" }}>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Essentials</h2>
              {(workerName || workerCode) && (
                <p className="text-xs text-gray-400 mt-1">{workerName}</p>
              )}
              <p className="text-xs text-gray-400 mt-1">{workerCode ? `${workerCode}` : ""}</p>
            </div>
            <button onClick={handleClose} className="text-sm font-semibold text-gray-500 hover:text-gray-700 transition-all">
              <X size={24} className="text-gray-600" />
            </button>
          </div>

          <div className="p-6 space-y-6">

            {/* Currently held / pending accessories */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-bold text-gray-800">Assigned Essentials</p>
                <button
                  type="button"
                  onClick={() => setShowAddModal(true)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white hover:opacity-90 transition-all"
                  style={{ background: `#${COLOR}` }}
                >
                  Add Essentials
                </button>
              </div>

              {loadingRecords ? (
                <p className="text-sm text-gray-400 text-center py-10">Loading Essentials...</p>
              ) : occupiedItems.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-10">Not holding any Essentials.</p>
              ) : (
                <div className="w-full">
                  <div className="max-h-64 overflow-y-auto overflow-x-auto relative border border-gray-100 rounded-lg">
                    <table className="w-full text-sm border-collapse">
                      <thead className="sticky top-0 bg-white shadow-sm z-[5]">
                        <tr className="bg-gray-100">
                          <th className="p-2 text-left">Name</th>
                          <th className="p-2 text-center">Assigned On</th>
                          <th className="p-2 text-center">Assigned By</th>
                          <th className="p-2 text-center">Price</th>
                          <th className="p-2 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {occupiedItems.map((item) => (
                          <tr key={item._id} className="border-b hover:bg-gray-50">
                            <td className="p-2 text-left font-medium text-gray-800">
                              <div className="flex gap-2">
                                <AccessoryLogo src={item.accessoryLogo} alt={item.accessoryName} />
                                <span>{item.accessoryName}</span>
                              </div>
                            </td>
<td className="p-2 sm:p-3 text-center">
  <div className="flex flex-col items-center justify-center">
    <div className="flex items-center justify-center">
      <span className="text-xs">
        {formatDate(item.assignedOn)}
      </span>
    </div>
    <span className="text-xs text-gray-600">
      {formatTime(item.assignedOn)}
    </span>
  </div>
</td>
                            <td className="p-2 text-center text-gray-500">{item.assignedBy || "-"}</td>
                            <td className="p-2 text-center font-semibold text-gray-700">
                              ₹{item.originalValue ?? 0}
                            </td>
                            <td className="p-2 text-center">
                              <div className="flex items-center justify-center gap-2">
                                {item.returnable && 
                                item.status=="alloted" &&
                                 isWithinRefundWindow(item.allotedOn || item.assignedOn) && 
                                (
                                  <button
                                    type="button"
                                    onClick={() => setRefundItem(item)}
                                    className="px-2.5 py-1 rounded-md text-xs font-semibold border transition-all"
                                    style={{ color: `#${COLOR}`, borderColor: "#BFDBFE", background: "#EFF6FF" }}
                                  >
                                    Refund
                                  </button>
                                )}

                                {
                                item.status=="pending" &&
                                (
                                   <button
                                  type="button"
                                  onClick={() => setDeleteItem(item)}
                                  className="px-2.5 py-1 rounded-md text-xs font-semibold border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 transition-all"
                                >
                                  Delete
                                </button>
                                )}
                                
                               
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Taken history (returned / refunded) */}
{/* History — all records, any status */}
<div className="border-t border-gray-100 pt-5">
  <p className="text-sm font-bold text-gray-800 mb-3">History</p>
  {loadingRecords ? (
    <p className="text-sm text-gray-400 text-center py-6">Loading history...</p>
  ) : records.length === 0 ? (
    <p className="text-xs text-gray-400 text-center py-4">No history yet</p>
  ) : (
    <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
      {records.map(normalize).map((h) => {
        const statusColors = {
          pending: "bg-amber-50 text-amber-600",
          alloted: "bg-blue-50 text-blue-600",
          returned: "bg-emerald-50 text-emerald-600",
          refunded: "bg-purple-50 text-purple-600",
        };
        const badgeClass = statusColors[h.status] || "bg-gray-100 text-gray-500";
        const hasReturnInfo = h.status === "returned" || h.status === "refunded";
        const hasRefundInfo = h.status === "refunded";

        return (
          <div key={h._id} className="bg-gray-50 border border-gray-100 rounded-lg px-4 py-3">
            {/* <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide mb-2 ${badgeClass}`}>
              {h.status || "-"}
            </span> */}

            <div className="flex items-start gap-2">
              <AccessoryLogo src={h.accessoryLogo} alt={h.accessoryName} />
              <div className="text-xs text-gray-600 space-y-0.5">
                {/* Line 1: assigned */}
                <p>
                  <span className="font-semibold text-gray-800">{h.accessoryName}</span>{" "}
                  <br />
                  Assigned on{" "}
                  <span className="font-medium">
                    {formatDate(h.assignedOn)} {formatTime(h.assignedOn)}
                  </span>{" "}
                  by <span className="font-medium">{h.assignedBy || "-"}</span>
                </p>

                {/* Line 2: alloted */}
                {h.allotedOn && (
                  <p>
                    {"₹"}{h.originalValue}
                    {" "}deducted on{" "}
                    <span className="font-medium">
                      {formatDate(h.allotedOn)} {formatTime(h.allotedOn)}
                    </span>
                  </p>
                )}

                {/* Line 3: returned */}
                {hasReturnInfo && h.returnedOn && (
                  <p>
                    Returned on{" "}
                    <span className="font-medium">
                      {formatDate(h.returnedOn)} {formatTime(h.returnedOn)}
                    </span>{" "}
                    by <span className="font-medium">{h.refundBy || "-"}</span>
                    {h.remarks && <span className="text-gray-400"> ({h.remarks})</span>}
                  </p>
                )}

                {/* Line 4: refunded */}
                {hasRefundInfo && h.refundedOn && (
                  <p>
                    <span className="font-medium">₹{h.refundedValue}</span>
                    {" "}Refunded on{" "}
                    <span className="font-medium">
                      {formatDate(h.refundedOn)} {formatTime(h.refundedOn)}
                    </span>{" "}
                    ·
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  )}
</div>

          </div>
        </div>
      </div>

      {showAddModal && (
        <AddAccessoryModal workerId={workerId} onClose={() => setShowAddModal(false)} onAssigned={refreshLists} />
      )}

      {refundItem && (
        <RefundModal
          item={refundItem}
          workerId={workerId}
          onClose={() => setRefundItem(null)}
          onRefunded={refreshLists}
        />
      )}

      {deleteItem && (
        <DeleteConfirmModal
          item={deleteItem}
          isDeleting={isDeleting}
          onClose={() => setDeleteItem(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
};

export default AccessoryManageModal;